<template>
  <div class="p-2">
    <!-- HEADER -->
    <div class="flex justify-between items-center pb-3 mb-4 border-b border-gray-200">
      <h4 class="font-bold text-xl text-gray-800 m-0">{{ $t('orgDashboard.talentPool.AddMembers') }}</h4>
      
      <div 
        class="cursor-pointer hover:bg-gray-100 rounded-full p-1 transition-transform duration-300 hover:rotate-90 flex items-center justify-center" 
        @click="$emit('close')"
      >
        <vs-icon icon="close" size="20px" color="#636363"></vs-icon>
      </div>
    </div>

    <!-- BODY: Form Inputs -->
    <div>
      <vs-row>
        <vs-col vs-w="6" vs-xs="12" class="p-2">
          <vs-input
            class="w-full"
            :label="$t('orgDashboard.talentPool.firstName')"
            v-model="firstName"
            v-validate="'required|alpha_num'"
            name="firstname"
            :placeholder="$t('orgDashboard.talentPool.firstName')"
          />
          <span class="text-danger text-sm" v-show="errors.has('firstname')">{{ errors.first('firstname') }}</span>
        </vs-col>

        <vs-col vs-w="6" vs-xs="12" class="p-2">
          <vs-input
            class="w-full"
            :label="$t('orgDashboard.talentPool.lastName')"
            v-model="lastName"
            v-validate="'required|alpha_num'"
            name="lastname"
            :placeholder="$t('orgDashboard.talentPool.lastName')"
          />
          <span class="text-danger text-sm" v-show="errors.has('lastname')">{{ errors.first('lastname') }}</span>
        </vs-col>
      </vs-row>

      <vs-row>
        <vs-col vs-w="6" vs-xs="12" class="p-2">
          <vs-input
            class="w-full"
            type="email"
            :label="$t('orgDashboard.talentPool.email')"
            v-model="email"
            v-validate="'required|email'"
            name="email"
            :placeholder="$t('orgDashboard.talentPool.email')"
          />
          <span class="text-danger text-sm" v-show="errors.has('email')">{{ errors.first('email') }}</span>
        </vs-col>

        <!-- <vs-col vs-w="6" vs-xs="12" class="p-2">
          <vs-input
            class="w-full"
            :label="$t('orgDashboard.talentPool.roleTitle')"
            v-model="roleTitle"
            v-validate="'required'"
            name="roleTitle"
            :placeholder="$t('orgDashboard.talentPool.roleTitle')"
          />
          <span class="text-danger text-sm" v-show="errors.has('roleTitle')">{{ errors.first('roleTitle') }}</span>
        </vs-col> -->
      
        <vs-col vs-w="6" vs-xs="12" class="p-2">
          <div class="vs-component vs-con-input-label vs-input w-full">
            <label class="vs-input--label">{{ $t('orgDashboard.talentPool.accessLevel') }}</label>
            <vs-select
              class="w-full"
              v-model="accessLevel"
              v-validate="'required'"
              name="accessLevel"
              width="100%"
            >
              <vs-select-item 
                v-for="(role) of roles" 
                :key="role.key" 
                :text="role.label" 
                :value="role.key" 
              />
            </vs-select>
            <span class="text-danger text-sm" v-show="errors.has('accessLevel')">{{ errors.first('accessLevel') }}</span>
          </div>
        </vs-col>
      </vs-row>
    </div>

    <!-- FOOTER -->
    <div class="flex justify-end mt-6 pt-2">
      <vs-button
        color="warning"
        type="filled"
        icon-pack="feather"
        icon="icon-check"
        class="font-semibold px-8"
        @click="addEmployee"
        :disabled="isSubmitting"
      >
        {{ $t('orgDashboard.talentPool.AddMembers') }}
      </vs-button>
    </div>

  </div>
</template>

<script>
import axios from 'axios'; // Ensure axios is imported

// Helper to get Python API URL (adjust logic to your env setup)
const PYTHON_API_URL = process.env.VUE_APP_API_URL;

export default {
  name: "AddEmployeeCardPopup",
  props: {
    roles: {
      type: Array,
      required: true,
    },
  },
  data() {
    return {
      firstName: '',
      lastName: '',
      email: '',
      roleTitle: '',
      accessLevel: '',
      isSubmitting: false,
    };
  },
  computed: {
    organizationId() {
      return this.$store.state.organization && this.$store.state.organization.orgData 
        ? this.$store.state.organization.orgData._id 
        : null;
    }
  },
  mounted() {
    document.addEventListener("keydown", this.handleEscKey);
  },
  beforeDestroy() {
    document.removeEventListener("keydown", this.handleEscKey);
  },
  methods: {
    handleEscKey(e) {
      if (e.key === "Escape") {
        this.$emit('close');
      }
    },
    resetForm() {
      this.firstName = '';
      this.lastName = '';
      this.email = '';
      this.roleTitle = '';
      this.accessLevel = '';
      this.$validator.reset();
    },
    addEmployee() {
      this.$validator.validateAll().then(result => {
        if (!result) return;

        this.isSubmitting = true;
        
        // 1. Data for Database (Node.js/Vuex)
        const payload = {
          organizationId: this.organizationId,
          firstName: this.firstName,
          lastName: this.lastName,
          email: this.email,
          roleTitle: this.roleTitle,
          accessLevel: this.accessLevel,
          status: 'ACTIVE',
        };

        // 2. Call existing Vuex action to Add User to DB
        this.$store.dispatch("organization/addEmployee", payload)
          .then(async (res) => {
            if (res && (res.code === "EMPLOYEE_ADDED_SUCCESSFULLY" || res.status === "SUCCESS" || res.code === "EMPLOYEE_ALREADY_EXISTS")) {
              
              // 3. TRIGGER EMAIL: Call Python Backend API
              try {
                const invitePayload = {
                  email: this.email,
                  first_name: this.firstName,
                  last_name: this.lastName,
                  role_title: this.roleTitle,
                  organization_id: this.organizationId,
                  api_token: localStorage.getItem('accessToken') // Send token for validation
                };

                await axios.post(`${PYTHON_API_URL}/api/talent-pool/manual-invite`, invitePayload);
                
                this.$vs.notify({
                  title: 'Success',
                  text: res.message || 'Member added and invite sent!',
                  color: 'success'
                });

              } catch (inviteError) {
                console.error("Invite Email Failed:", inviteError);
                this.$vs.notify({
                  title: 'Warning',
                  text: 'Member added, but email invite failed to send.',
                  color: 'warning'
                });
              }

              this.resetForm();
              this.$emit('close');
            } else {
               this.$vs.notify({
                title: 'Notification',
                text: res.message || 'Action completed',
                color: 'primary'
              });
            }
          })
          .catch(err => {
            console.error("Add employee error:", err);
            this.$vs.notify({
              title: 'Error',
              text: err.message || 'Failed to add member',
              color: 'danger'
            });
          })
          .finally(() => {
            this.isSubmitting = false;
          });
      });
    },
  },
};
</script>

<style scoped>
/* Align vs-input label color */
.vs-input--label {
  color: #6b7280; 
}
</style>